import { createContext, useState, useContext, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Collapse,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  ErrorOutline,
  Info,
  CheckCircle,
  WarningAmber,
  ExpandMore
} from '@mui/icons-material';
import { logError, trySendStoredErrors } from './errorLogger';
import { registerSnackbar } from './Helpers/snackbarExternalHelper';

const SnackbarContext = createContext();
export const useSnackbar = () => useContext(SnackbarContext);

export const SnackbarProvider = ({ children }) => {
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  const [showDetails, setShowDetails] = useState(false);

  const showSnackbar = (message, severity = 'error') => {
    setSnackbar({ open: true, message, severity });
    setShowDetails(false);
  };

  const handleClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
    setShowDetails(false);
  };

  useEffect(() => {
    trySendStoredErrors();
  }, []);

  useEffect(() => {
    if (snackbar.open && snackbar.message) {
      if (process.env.NODE_ENV === 'development') {
        console.error('[Error capturado por Snackbar]:', snackbar.message);
      }
      logError(snackbar.message);
    }
  }, [snackbar]);

  useEffect(() => {
  registerSnackbar(showSnackbar);
}, []);

  const getIcon = (severity) => {
    switch (severity) {
      case 'error': return <ErrorOutline color="error" fontSize="large" />;
      case 'warning': return <WarningAmber color="warning" fontSize="large" />;
      case 'info': return <Info color="info" fontSize="large" />;
      case 'success': return <CheckCircle color="success" fontSize="large" />;
      default: return null;
    }
  };

  const getTechnicalDetails = () => {
    if (!snackbar.message) return '';
    if (snackbar.message instanceof Error) return snackbar.message.stack || snackbar.message.toString();
    if (typeof snackbar.message === 'object') return JSON.stringify(snackbar.message, null, 2);
    return String(snackbar.message);
  };

  return (
    <SnackbarContext.Provider value={{ showSnackbar }}>
      {children}
      <Dialog open={snackbar.open} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {getIcon(snackbar.severity)}
          {snackbar.severity === 'error' && 'Error del sistema'}
          {snackbar.severity === 'warning' && 'Advertencia'}
          {snackbar.severity === 'info' && 'Información'}
          {snackbar.severity === 'success' && 'Operación exitosa'}
        </DialogTitle>

        <DialogContent dividers>
          <Typography gutterBottom>
            {(snackbar.severity === 'error' || snackbar.severity === 'warning')
              ? 'El sistema no pudo devolver la información de la manera esperada. Verifique su conexión y si el problema persiste, consulte con un Administrador.'
              : 'El sistema tiene un mensaje para usted.'}
          </Typography>

          <Collapse in={showDetails} timeout="auto" unmountOnExit>
            <Box mt={2}>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                {getTechnicalDetails()}
              </Typography>
            </Box>
          </Collapse>
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'space-between' }}>
          {(snackbar.severity === 'error' || snackbar.severity === 'warning') && (
            <Tooltip title="Mostrar detalles técnicos">
              <IconButton
                size="small"
                onClick={() => setShowDetails(prev => !prev)}
                sx={{
                  transform: showDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                }}
              >
                <ExpandMore fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Button onClick={handleClose} autoFocus variant="contained">
            Aceptar
          </Button>
        </DialogActions>
      </Dialog>
    </SnackbarContext.Provider>
  );
};
